import { useEffect } from 'react';
import { getAddressForm } from '@repo/lib/address';
import { Flex } from '@repo/ui/Flex';
import { useFormContext, type Path, Controller } from 'react-hook-form';
import type { WorkflowData } from '../../workflow';
import { InputRoot } from '@repo/ui/Input/InputRoot';
import { InputGroup } from '@repo/ui/Input/InputGroup';
import { InputField } from '@repo/ui/Input/InputField';
import { InputLabel } from '@repo/ui/Input/InputLabel';
import { InputControl } from '@repo/ui/Input/InputControl';
import { Text, rawString } from '../Language/Language';
import { Dropdown } from '../Dropdown/Dropdown';
import s from './AddressForm.module.css';

export type AddressFormProps = {
  name: Path<WorkflowData>;
  countryCode: string;
  baseLabel: string;
};

export const AddressForm = (props: AddressFormProps) => {
  const { name, countryCode, baseLabel } = props;
  const { control, register, setValue } = useFormContext<WorkflowData>();
  const form = getAddressForm(countryCode);

  useEffect(() => {
    setValue(`${name}.countryCode` as Path<WorkflowData>, countryCode);
  }, [name, countryCode, setValue]);

  return (
    <Flex direction="column" gap="g-8">
      <h3 id="addressFormTitle" className={s.title}>
        <Text langKey={`${baseLabel}.header`} />
      </h3>

      {form.layout.map((row, idx) => (
        <Flex direction="row" key={idx} gap="g-8">
          {row.map((field) => {
            const id = `${name}.${field}` as Path<WorkflowData>;

            return field === 'province' && form.provinces ? (
              <Controller
                key={id}
                name={id}
                control={control}
                defaultValue=""
                render={({ field: controlField }) => (
                  <Dropdown
                    className={s.select}
                    label={rawString({ langKey: `${baseLabel}.${field}` })}
                    labelVariant="embedded"
                    placeholder={rawString({
                      langKey: `${baseLabel}.${field}`,
                    })}
                    options={form.provinces!.map((value) => ({
                      value,
                      label: value,
                    }))}
                    value={
                      typeof controlField.value === 'string'
                        ? controlField.value
                        : ''
                    }
                    onValueChange={controlField.onChange}
                    onBlur={controlField.onBlur}
                    name={controlField.name}
                  />
                )}
              />
            ) : (
              <InputRoot key={id} width="full">
                <InputGroup>
                  <InputField>
                    <InputLabel htmlFor={id}>
                      <Text langKey={`${baseLabel}.${field}`} />
                    </InputLabel>
                    <InputControl id={id} {...register(id)} />
                  </InputField>
                </InputGroup>
              </InputRoot>
            );
          })}
        </Flex>
      ))}
    </Flex>
  );
};
